import useIsMobile from 'hooks/useIsMobile'
import React, { useState, useEffect, useRef, useMemo, memo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import Slider from 'react-slick'
import { FaChevronCircleLeft, FaChevronCircleRight } from 'react-icons/fa'

const FilterTags = memo(
  ({ activeTab, setActiveTab, tagsList, styleTags = {}, assetsFolder, carousel = false, classNames, fullWidth }) => {
    const user = useSelector((state) => state.auth.user)
    const isMobile = useIsMobile()
    const [slidesToShow, setSlidesToShow] = useState(1) // Default to 1 slide
    const sliderContainerRef = useRef(null)
    const slideWidth = fullWidth ? 75 : 70 // Set the width of each slide

    // Memoize filtered tags to prevent unnecessary calculations
    const filteredTags = useMemo(() => {
      if (!user) {
        return tagsList.filter((key) => key !== 'My Uploads' && key !== 'My Results')
      }
      return tagsList
    }, [tagsList, user])

    // Handle tab selection with useCallback
    const handleSelectTab = useCallback(
      (key) => () => {
        setActiveTab(key)
      },
      [setActiveTab]
    )

    const calculateSlidesToShow = useCallback(() => {
      if (sliderContainerRef.current) {
        const containerWidth = sliderContainerRef.current.offsetWidth
        const slides = Math.floor(containerWidth / slideWidth)
        setSlidesToShow(Math.max(1, slides)) // Ensure at least 1 slide
      }
    }, [slideWidth])

    // Add resize observer instead of using window resize event
    useEffect(() => {
      if (carousel) {
        // Calculate on initial load
        calculateSlidesToShow()

        // Use ResizeObserver for better performance
        const resizeObserver = new ResizeObserver(calculateSlidesToShow)
        if (sliderContainerRef.current) {
          resizeObserver.observe(sliderContainerRef.current)
        }

        // Cleanup on unmount
        return () => {
          resizeObserver.disconnect()
        }
      }
    }, [carousel, calculateSlidesToShow])

    // Memoize arrow components to prevent recreation on each render
    const ArrowLeft = useMemo(() => {
      return function LeftArrow({ onClick }) {
        return (
          <button aria-label="left" className="flex items-center mb-6" onClick={onClick}>
            <FaChevronCircleLeft />
          </button>
        )
      }
    }, [])

    const ArrowRight = useMemo(() => {
      return function RightArrow({ onClick }) {
        return (
          <button aria-label="right" className="flex items-center mb-6" onClick={onClick}>
            <FaChevronCircleRight />
          </button>
        )
      }
    }, [])

    // Memoize slider settings
    const sliderSettings = useMemo(
      () => ({
        button: true,
        swipeToSlide: true,
        slidesToShow: slidesToShow,
        slidesToScroll: isMobile ? 1 : 5,
        infinite: false,
        prevArrow: <ArrowLeft />,
        nextArrow: <ArrowRight />,
      }),
      [slidesToShow, isMobile, ArrowLeft, ArrowRight]
    )

    // Render tag item to avoid duplication
    const renderTag = useCallback(
      (key, index) => {
        const isActive = activeTab === key

        return (
          <div
            className={`flex flex-col cursor-pointer pb-1 h-9 ${isActive ? 'selected-filter relative border-b-4 border-solid opacity-100 border-black' : 'opacity-80'}`}
            key={`filter-tag-${key}-${index}`}
            style={styleTags}
            onClick={handleSelectTab(key)}
          >
            <div className="flex flex-col gap-1 justify-center items-center md:gap-2">
              {assetsFolder && (
                <img
                  src={`/assets/${assetsFolder}/${key.toLowerCase()}.svg`}
                  className="!w-4 !h-4 md:!w-6 md:!h-6 !object-contain"
                  width={24}
                  height={24}
                  alt={key}
                  loading="eager"
                />
              )}
              <span className={`whitespace-nowrap ${isActive ? 'text-black font-black' : ''}`}>{key}</span>
            </div>
          </div>
        )
      },
      [activeTab, styleTags, assetsFolder, handleSelectTab]
    )

    if (!tagsList.length) {
      return null
    }

    if (carousel) {
      return (
        <div
          ref={sliderContainerRef}
          className={`filter-model-tags w-[82%] md:w-[calc(100%-200px)] gap-2 px-0 ${classNames}`}
        >
          <Slider {...sliderSettings} className="flex gap-1">
            {filteredTags.map((key, i) => renderTag(key, i))}
          </Slider>
        </div>
      )
    }

    return (
      <div className={`flex overflow-auto gap-3 px-8 w-full md:gap-6 md:pl-[40px] md:pr-[15px] ${classNames}`}>
        {filteredTags.map((key, i) => renderTag(key, i))}
      </div>
    )
  }
)

FilterTags.displayName = 'FilterTags'

export default FilterTags
